import React, { useEffect, useState } from 'react';
import { FormControl, TextField, Chip, Tooltip, Autocomplete, IconButton } from '@mui/material';
import ModalV2 from 'src/components/ModalV2';
import {
  MANAGE_PROFILE_GET_USER_DETAILS,
  MANAGE_PROFILE_UPDATE_USER_DETAILS,
  GET_SHARE_FEEDBACK_URL,
} from 'src/constants/httpEndPoints';
import { useToast } from 'src/hooks/useToast';
import { apiClient } from 'src/api/apiClient';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { setLinkedInConfigured, setUserTimezone } from 'src/store/login';
import { trackProfileFeedback } from 'src/mix-panel/profile/functions';
import { AppDispatch } from 'src/app/rootStore';
import { useProfile } from 'src/hooks/useProfile';
import { TimezoneOptionProp, timezoneOptionsWithOffsets } from 'src/utils/timezoneCountries';
import { ReactComponent as CrossIcon } from 'src/assets/icons/cross-icon-black.svg';

type ManageProfileProps = {
  setOpenManageProfile: React.Dispatch<React.SetStateAction<boolean>>;
  openManageProfile: boolean;
  setDataRefresh?: React.Dispatch<React.SetStateAction<number>>;
};

type AliasEmailIdsProps = {
  value: string;
};

const ManageProfile = ({
  setOpenManageProfile,
  openManageProfile,
  setDataRefresh,
}: ManageProfileProps) => {
  const [save, setSave] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState(false);
  const [firstNameErrorMessage, setFirstNameErrorMessage] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState(false);
  const [lastNameErrorMessage, setLastNameErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [primaryEmailId, setPrimaryEmaidId] = useState('');
  const [userRole, setUserRole] = useState<number>(0);
  const [userRoleName, setUserRoleName] = useState<string>('');
  const [designatedGeo, setDesignatedGeo] = useState('');
  const [aliasEmailIdsArray, setAliasEmailIdsArray] = useState<any>([]);
  const [aliasEmailId, setAliasEmailId] = useState<string>('');
  const [aliasEmailIdError, setAliasEmailIdError] = useState(false);
  const [aliasEmailIdErrorMessage, setAliasEmailIdErrorMessage] = useState('');
  const [linkedInId, setLinkedInId] = useState('');
  const [linkedInError, setLinkedInError] = useState(false);
  const [linkedInErrorMessage, setLinkedInErrorMessage] = useState('');
  const [userId, setUserId] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [g2Url, setG2Url] = useState('');
  const [showShareFeedback, setShowShareFeedback] = useState(false);
  const FeatureFlags = useFeatureFlags();
  const { emailId, customerId, clientName } = useProfile();
  const toast = useToast();
  const [timezoneValue, setTimezoneValue] = useState<TimezoneOptionProp | null>(null);

  const nameRegEx = /^(?=.{1,50}$)[a-zA-Z]+(?: [a-zA-Z]+)*$/;
  const emailRegEx = /^[^?,@]+@[^?,@]+\.[^?,@]+$/;
  const linkedInRegEx =
    /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|pub|company)\/[a-zA-Z0-9-_%]+\/?$/;
  const dispatch = useDispatch<AppDispatch>();
  const timezone = useSelector((state: any) => state.user.userTimezone);
  React.useEffect(() => {
    if (timezone) {
      const selectedTimezoneValue = timezoneOptionsWithOffsets.find(
        (item) => item.value === timezone,
      );
      setTimezoneValue(selectedTimezoneValue || null);
    } else {
      const selectedTimezoneValue = timezoneOptionsWithOffsets.find((item) => item.value === 'UTC');
      setTimezoneValue(selectedTimezoneValue || null);
    }
  }, [timezone]);

  const timezones = React.useMemo(() => {
    return timezoneOptionsWithOffsets.sort((a, b) => a.value.localeCompare(b.value));
  }, []);

  const validateName = (name: string) => {
    return nameRegEx.test(name);
  };

  const validateEmail = (email: string) => {
    return emailRegEx.test(email);
  };

  const validateLinkedIn = (url: string) => {
    return linkedInRegEx.test(url);
  };

  useEffect(() => {
    getUserDetails();
    if (FeatureFlags.g2_enabled) {
      getG2URL();
    }
  }, []);

  const getUserDetails = () => {
    setLoading(true);
    const payload = { email: emailId };
    return apiClient
      .post(MANAGE_PROFILE_GET_USER_DETAILS, payload)
      .then((response: any) => {
        if (response.data.status === 'success') {
          setUserId(response.data.obj.userId);
          if (response.data.obj.firstName) {
            setFirstName(response.data.obj.firstName);
          } else {
            setFirstNameError(true);
            setFirstNameErrorMessage('First Name is required*');
          }
          setLastName(response.data.obj.lastName ? response.data.obj.lastName : '');
          setPrimaryEmaidId(response.data.obj.email);
          setAliasEmailIdsArray(
            response.data.obj.aliasEmail ? response.data.obj.aliasEmail.split(',') : [],
          );
          setUserRole(response.data.obj.role);
          setUserRoleName(response.data.obj.roleName);
          setDesignatedGeo(response.data.obj.designatedGeo);
          setLinkedInId(response.data.obj.linkedInUrl || '');
        } else {
          toast.error('Error', response.data.message);
          setOpenManageProfile(false);
        }
      })
      .catch((error: any) => {
        toast.error('Error', error.response.data.error);
        setOpenManageProfile(false);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (save) {
      updateUserDetails();
      setSave(false);
    }
  }, [save]);

  const updateUserDetails = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('email', primaryEmailId);
    formData.append('firstName', firstName.trim());
    formData.append('middleName', '');
    formData.append('lastName', lastName.trim());
    formData.append('role', userRole.toString());
    formData.append('updatedByEmail', `${emailId}`);
    formData.append('userId', userId.toString());
    formData.append('designatedGeo', designatedGeo);
    formData.append('aliasEmail', aliasEmailIdsArray.join(','));
    if (linkedInId?.length > 0) {
      formData.append('linkedInUrl', linkedInId);
    }
    formData.append('timeZone', timezoneValue?.value ? timezoneValue.value : '');
    return apiClient
      .post(MANAGE_PROFILE_UPDATE_USER_DETAILS, formData)
      .then((response: any) => {
        if (response.status === 200 || response.data.status === 'success') {
          toast.success('Success', response.data);
          if (timezoneValue) {
            dispatch(setUserTimezone({ timezone: timezoneValue.value }));
          } else {
            dispatch(setUserTimezone({ timezone: '' }));
          }
          setOpenManageProfile(false);
        } else {
          toast.error('Error', response.data.message);
          setOpenManageProfile(false);
        }
        dispatch(setLinkedInConfigured(linkedInId));
        setDataRefresh && setDataRefresh((prev) => prev + 1);
      })
      .catch((error: any) => {
        toast.error('Error', error.response.data.error);
        setOpenManageProfile(false);
      })
      .finally(() => setLoading(false));
  };

  const handleBlur = () => {
    const newAliasEmailId = aliasEmailId?.trim();
    const invalidEmails: string[] = [];
    let hasError = false;
    const haveEmailIds = newAliasEmailId
      .replace(' ', '')
      .split(',')
      .filter((email: string) => email);
    if (haveEmailIds.length > 0) {
      haveEmailIds?.map((emailId) => {
        if (!hasError) {
          if (validateEmail(emailId)) {
            if (aliasEmailIdsArray.includes(emailId)) {
              setAliasEmailIdError(true);
              setAliasEmailIdErrorMessage('This email ID is already in the list.');
              invalidEmails.push(emailId);
              setAliasEmailId(invalidEmails.join(','));
              hasError = true;
            } else {
              aliasEmailIdsArray.push(emailId);
              setAliasEmailIdsArray(aliasEmailIdsArray);
              setAliasEmailId('');
              setAliasEmailIdError(false);
              setAliasEmailIdErrorMessage('');
            }
          } else {
            setAliasEmailIdError(true);
            setAliasEmailIdErrorMessage('Invalid email ID');
            invalidEmails.push(emailId);
            setAliasEmailId(invalidEmails.join(','));
            hasError = true;
          }
        } else {
          invalidEmails.push(emailId);
          setAliasEmailId(invalidEmails.join(','));
        }
      });
    } else {
      setAliasEmailId('');
      setAliasEmailIdError(false);
      setAliasEmailIdErrorMessage('');
    }
  };

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const newAliasEmailId = aliasEmailId?.trim();
      const invalidEmails: string[] = [];
      let hasError = false;
      const haveEmailIds = newAliasEmailId
        .replace(' ', '')
        .split(',')
        .filter((email: string) => email);

      if (haveEmailIds.length > 0) {
        haveEmailIds.map((emailId) => {
          if (!hasError) {
            if (validateEmail(emailId)) {
              if (aliasEmailIdsArray.includes(emailId)) {
                setAliasEmailIdError(true);
                setAliasEmailIdErrorMessage('This email ID is already in the list.');
                invalidEmails.push(emailId);
                setAliasEmailId(invalidEmails.join(','));
                hasError = true;
              } else {
                aliasEmailIdsArray.push(emailId);
                setAliasEmailIdsArray(aliasEmailIdsArray);
                setAliasEmailId('');
                setAliasEmailIdError(false);
                setAliasEmailIdErrorMessage('');
              }
            } else {
              setAliasEmailIdError(true);
              setAliasEmailIdErrorMessage('Invalid email ID');
              invalidEmails.push(emailId);
              setAliasEmailId(invalidEmails.join(','));
              hasError = true;
            }
          } else {
            invalidEmails.push(emailId);
            setAliasEmailId(invalidEmails.join(','));
          }
        });
      } else {
        setAliasEmailId('');
        setAliasEmailIdError(false);
        setAliasEmailIdErrorMessage('');
      }
    } else if (event.key === 'Backspace' && !aliasEmailId) {
      setAliasEmailIdsArray(aliasEmailIdsArray.slice(0, -1));
    }
  };

  const handleDelete = (chipToDelete: string) => () => {
    setAliasEmailIdsArray((chips: AliasEmailIdsProps[]) =>
      chips.filter((chip: any) => chip !== chipToDelete),
    );
  };

  const handleClosePopup = () => {
    setOpenManageProfile(false);
  };
  const handleSave = () => {
    if (validateName(firstName.trim())) {
      if (!firstNameError && !lastNameError && !aliasEmailIdError) {
        setSave(true);
      }
    }
  };

  const handleFirstName = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFirstName(event.target.value);
    const trimmed = event.target.value.trim();
    if (trimmed === '') {
      setFirstNameError(true);
      setFirstNameErrorMessage('First Name is required*');
    } else if (!validateName(trimmed)) {
      setFirstNameError(true);
      setFirstNameErrorMessage('Invalid first name');
    } else {
      setFirstNameError(false);
      setFirstNameErrorMessage('');
    }
  };

  const handleLastName = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLastName(event.target.value);
    const trimmed = event.target.value.trim();
    if (!validateName(trimmed)) {
      setLastNameError(true);
      setLastNameErrorMessage('Invalid last name');
    } else {
      setLastNameError(false);
      setLastNameErrorMessage('');
    }
  };

  const handleLinkedInUrl = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLinkedInId(event.target.value.trim());
    if (!event.target.value.trim()) {
      setLinkedInError(false);
      setLinkedInErrorMessage('');
    } else {
      if (!validateLinkedIn(event.target.value.trim())) {
        setLinkedInError(true);
        setLinkedInErrorMessage('Invalid Linkedin Url');
      } else {
        setLinkedInError(false);
        setLinkedInErrorMessage('');
      }
    }
  };

  const fewAliasEmailIdsArray: string[] = [];
  aliasEmailIdsArray.map((email: string) => {
    if (fewAliasEmailIdsArray.length < 2) {
      fewAliasEmailIdsArray.push(email);
    }
  });

  const arrayLength = aliasEmailIdsArray.length - 2;

  const handleShowMore = () => setShowMore((prev) => !prev);
  const getG2URL = () => {
    setLoading(true);
    const payload = { emailID: emailId, uniqueID: customerId };
    return apiClient
      .post(GET_SHARE_FEEDBACK_URL, payload)
      .then((response: any) => {
        if (response.status === 200) {
          setG2Url(response.data);
          setShowShareFeedback(true);
        } else {
          setShowShareFeedback(false);
        }
        setDataRefresh && setDataRefresh((prev) => prev + 1);
      })
      .catch((error: any) => {
        toast.error('Error', error.response.data.error);
        setShowShareFeedback(false);
      })
      .finally(() => setLoading(false));
  };
  const handleTimezoneChange = (zone: TimezoneOptionProp | null) => {
    setTimezoneValue(zone);
  };
  const handleClearTimezone = () => {
    const selectedTimezoneValue = timezoneOptionsWithOffsets.find((item) => item.value === 'UTC');
    setTimezoneValue(selectedTimezoneValue || null);
  };
  return (
    <>
      <ModalV2
        open={openManageProfile}
        onClose={handleClosePopup}
        fullWidth={true}
        maxWidth="sm"
        type="modal"
        contentDivider={true}
        header="Edit Profile"
        subheader="Manage information about yourself and other relevant details."
        closeActionConfig={{
          label: 'Cancel',
          handleAction: handleClosePopup,
          id: 'settings-manage-profile-modal-close-button',
        }}
        submitActionConfig={{
          label: 'Save',
          handleAction: handleSave,
          disabled:
            firstNameError || lastNameError || aliasEmailIdError || loading || linkedInError,
          loading: loading,
          id: 'settings-manage-profile-modal-save-button',
        }}
      >
        <div className="manage-profile-body-container">
          <FormControl>
            <div className="manage-profiles-names-section">
              <div className="manage-profiles-textfields-div">
                <TextField
                  id="outlined-basic"
                  size="small"
                  className="manage-profile-names"
                  required
                  label="First Name"
                  disabled={loading}
                  value={firstName === null ? '' : firstName}
                  onChange={handleFirstName}
                  error={firstNameError}
                  fullWidth
                  helperText={
                    firstName === null || !validateName(firstName.trim())
                      ? firstNameErrorMessage
                      : ''
                  }
                />
              </div>
              <div className="manage-profiles-textfields-div">
                <TextField
                  id="outlined-basic"
                  size="small"
                  className="manage-profile-names"
                  label="Last Name"
                  value={lastName === null ? '' : lastName}
                  onChange={handleLastName}
                  error={lastNameError}
                  helperText={!validateName(lastName.trim()) ? lastNameErrorMessage : ''}
                  disabled={loading}
                  fullWidth
                />
              </div>
            </div>

            <div className="manage-profiles-textfields-div">
              <TextField
                id="outlined-basic"
                size="small"
                className="manage-profile-text-fields"
                label="Primary email ID"
                value={primaryEmailId}
                disabled
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
              />
            </div>

            <div className="manage-profiles-textfields-div">
              {showMore ? (
                <Tooltip
                  className="manage-profile-tooltip"
                  title="Add multiple email ids separated by a comma (,)."
                >
                  <TextField
                    id="outlined-multiline-flexible"
                    size="small"
                    label="Alias email IDs"
                    value={aliasEmailId}
                    onChange={(event) => setAliasEmailId(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    error={aliasEmailIdError}
                    disabled={loading}
                    helperText={aliasEmailIdError && aliasEmailIdErrorMessage}
                    InputProps={{
                      startAdornment: (
                        <div className="manage-profiles-chips-container">
                          {aliasEmailIdsArray.map((option: string) => {
                            if (option !== '') {
                              return (
                                <Chip
                                  key={option}
                                  label={option}
                                  onDelete={handleDelete(option)}
                                  className="manage-profile-chip"
                                />
                              );
                            }
                          })}
                        </div>
                      ),
                      endAdornment:
                        aliasEmailIdsArray.length > 2 ? (
                          <span className="show-less-text" onClick={handleShowMore}>
                            show less
                          </span>
                        ) : null,
                      className: 'manage-profile-all-chips',
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  className="manage-profile-tooltip"
                  title="Add multiple email ids separated by a comma (,)."
                >
                  <TextField
                    id="outlined-multiline-static"
                    size="small"
                    label="Alias email IDs"
                    value={aliasEmailId}
                    onChange={(event) => setAliasEmailId(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    error={aliasEmailIdError}
                    disabled={loading}
                    helperText={aliasEmailIdError && aliasEmailIdErrorMessage}
                    InputProps={{
                      startAdornment: fewAliasEmailIdsArray.map((option: string) => {
                        if (option !== '') {
                          return (
                            <Chip
                              key={option}
                              label={option}
                              onDelete={handleDelete(option)}
                              className="manage-profile-chip"
                            />
                          );
                        }
                      }),
                      endAdornment:
                        aliasEmailIdsArray.length > fewAliasEmailIdsArray.length ? (
                          <span
                            className="show-more-text"
                            onClick={handleShowMore}
                          >{` + ${arrayLength} more`}</span>
                        ) : null,
                      className: 'manage-profile-limited-chips',
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <div className="manage-profile-geo-role">
              <div className="manage-profiles-textfields-div">
                <TextField
                  id="outlined-basic"
                  size="small"
                  className="manage-profile-text-fields"
                  label="Designated Geo"
                  defaultValue={designatedGeo}
                  value={designatedGeo}
                  disabled
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </div>

              <div className="manage-profiles-textfields-div">
                <TextField
                  id="outlined-basic"
                  size="small"
                  className="manage-profile-text-fields"
                  label="Role"
                  value={userRoleName}
                  disabled
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </div>
            </div>
            <div className="manage-profiles-textfields-div">
              <TextField
                id="outlined-basic"
                size="small"
                className="manage-profile-text-fields"
                label="Linkedin ID"
                value={linkedInId}
                onChange={handleLinkedInUrl}
                error={linkedInError}
                helperText={linkedInId && !validateLinkedIn(linkedInId) ? linkedInErrorMessage : ''}
                placeholder="Linkedin ID"
                fullWidth
              />
            </div>
            <div className="manage-profiles-textfields-div">
              <Autocomplete
                freeSolo
                size="small"
                id="manage-profiles-timezone-field"
                disableClearable
                autoHighlight
                value={timezoneValue || ''}
                onChange={(event: any, newValue: string | any | null) => {
                  handleTimezoneChange(newValue);
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Time Zone"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <IconButton
                            onClick={(event) => {
                              params.inputProps.onChange?.({
                                target: { value: '' },
                              } as unknown as React.ChangeEvent<HTMLInputElement>);
                              handleClearTimezone();
                            }}
                          >
                            <CrossIcon className="mp-timezone-cross-icon" />
                          </IconButton>
                        </>
                      ),
                    }}
                  />
                )}
                options={timezones}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  if (!option || !option.value) {
                    return '';
                  }
                  return `${option.value} : ${option.offset} (${option.label})`;
                }}
                renderOption={(props, option, index) => (
                  <li
                    {...props}
                    key={`${index}-${option.value}-${option.offset}`}
                    className={`mp-timezone-option${props['aria-selected'] ? ' selected' : ''}`}
                  >
                    <span className="mp-timezone-label">{`${option.value} : `}</span>
                    <span className="mp-timezone-offset">{`${option.offset} (${option.label})`}</span>
                  </li>
                )}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;

                  const filterValue = inputValue.toLowerCase();
                  return options.filter(
                    (option) =>
                      option.label.toLowerCase().includes(filterValue) ||
                      option.value.toLowerCase().includes(filterValue) ||
                      option.offset.toLowerCase().includes(filterValue) ||
                      option.abbreviation.toLowerCase().includes(filterValue),
                  );
                }}
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') {
                    return option.value === value;
                  }
                  return option.value === value?.value;
                }}
              />
            </div>
            {showShareFeedback && FeatureFlags.g2_enabled && (
              <div
                className="manage-profile-share-feedback"
                onClick={() => trackProfileFeedback(clientName)}
              >
                <a href={g2Url} target="_blank" rel="noreferrer">
                  <strong className="share-feedback">Share Your Feedback</strong>
                </a>
              </div>
            )}
          </FormControl>
        </div>
      </ModalV2>
    </>
  );
};

export default ManageProfile;
